import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterOutlet, NavigationEnd, Router } from '@angular/router';
import { Footer } from './components/navigation/footer/footer';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalModule, MsalService } from '@azure/msal-angular';
import { filter, Subject, takeUntil, map, tap, catchError, of } from 'rxjs';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { LoggingService } from './services/logging-service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MsalModule, Footer, NgxSpinnerModule, TranslatePipe],
  templateUrl: './app.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App implements OnInit, OnDestroy {

  private static readonly TOKEN_REFRESH_INTERVAL_MS = 3000_000;


  protected readonly title = signal('validPlus-front-end');
  isIframe = false;
  loginDisplay = false;
  private readonly _destroying$ = new Subject<void>();
  private previousUrl = '';
  private intervalId: ReturnType<typeof window.setInterval> | null = null;

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loggingService: LoggingService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.msalInit();
    this.routerPageTrack();
  }

  msalInit() {
    this.authService.handleRedirectObservable().subscribe();

    this.msalBroadcastService.msalSubject$
      .pipe(takeUntil(this._destroying$))
      .subscribe((msg: EventMessage) => {
        if (msg.eventType === EventType.LOGIN_SUCCESS) {
          this.startTokenRefreshCycle();
        }
      });

    this.isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
        if (this.authService.instance.getAllAccounts().length > 0) {
          this.startTokenRefreshCycle();
        }
      });
  }

  routerPageTrack(){
     this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((navigationEvent: NavigationEnd) => {
          let activeRoute = this.router.routerState.root;
          const segments: string[] = [];
          while (activeRoute.firstChild) {
            activeRoute = activeRoute.firstChild;
            if (activeRoute.snapshot.url.length) {
              segments.push(...activeRoute.snapshot.url.map((segment) => segment.path));
            }
          }
          const pageTitle = activeRoute.snapshot.data['title'] || 'Application Page';
          const fullPath = '/' + segments.join('/') || navigationEvent.urlAfterRedirects;

          return {
            title: pageTitle,
            url: fullPath,
            previousUrl: this.previousUrl,
            currentUrl: navigationEvent.urlAfterRedirects
          };
        }),
        takeUntil(this._destroying$)
      )
     .subscribe(({ title, url, previousUrl, currentUrl }) => {
        const userProps = this.getUserProperties();

        this.loggingService.logPageView(title, url, userProps);
        this.loggingService.logUserAction('page_navigation', {
          fromPage: previousUrl,
          toPage: currentUrl,
          pageTitle: title,
          ...userProps
        });

        this.previousUrl = currentUrl;
      });
  }

  private startTokenRefreshCycle() {
    if (this.intervalId) {
      return;
    }

    this.setupTokenRefresh();
  }

  private setupTokenRefresh() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.intervalId = window.setInterval(() => {
      this.refreshToken();
    }, App.TOKEN_REFRESH_INTERVAL_MS);
  }

  private refreshToken() {
    const account = this.getRefreshAccount();
    if (!account) {
      return;
    }

    const request: SilentRequest = {
      account,
      scopes: this.getRefreshScopes(),
      forceRefresh: true,
    };

    this.authService
      .acquireTokenSilent(request)
      .pipe(
        tap((response) => {
          this.updateStoredToken(response);
          console.log('Token refreshed at', new Date().toISOString());
        }),
        catchError((error) => {
          console.error('Error during token refresh:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private getRefreshAccount(): AccountInfo | null {
    return this.authService.instance.getActiveAccount() ?? this.authService.instance.getAllAccounts()[0] ?? null;
  }

  private getRefreshScopes(): string[] {
    if (!this.msalGuardConfig.authRequest || typeof this.msalGuardConfig.authRequest === 'function') {
      return [];
    }

    return this.msalGuardConfig.authRequest.scopes ?? [];
  }

  private updateStoredToken(response: AuthenticationResult | null) {
    const token = response?.idToken ?? response?.accessToken;

    if (!token) {
      return;
    }

    localStorage.setItem('TokenKey', JSON.stringify(token));
  }

  private getUserProperties(): Record<string, any> {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      const claims = account.idTokenClaims as any;

      return {
        userEmail: claims?.email || claims?.preferred_username || account.username,
        userName: claims?.name || claims?.given_name || 'Unknown User',
        userId: account.localAccountId,
        userPrincipalName: claims?.upn
      };
    }

    return {
      userEmail: 'Anonymous',
      userName: 'Anonymous User'
    };
  }

  setLoginDisplay() {
    this.loginDisplay = this.authService.instance.getAllAccounts().length > 0;
    if (this.loginDisplay) {
      this.saveTokenClaimsToLocalStorage();
    }
  }

   checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      const accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest) {
      this.authService
        .loginRedirect({
          ...this.msalGuardConfig.authRequest,
        } as RedirectRequest)
        .subscribe((response) => {
          console.log(response);
        });
    } else {
      this.authService.loginRedirect();
    }
  }

  logout(popup = false) {
    this.loggingService.clearUser();

    if (popup) {
      this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      this.authService.logoutRedirect();
    }
  }

  private saveTokenClaimsToLocalStorage() {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length > 0) {
      const account = accounts[0];
      this.loggingService.setUser(account.username || account.localAccountId);
      this.loggingService.logEvent('User Login', {
        accountId: account.localAccountId,
        environment: account.environment
      });

      const idTokenClaims = account.idTokenClaims;
      const idTokens = account.idToken;
      if (idTokenClaims) {
        localStorage.setItem('TokenClaims', JSON.stringify(idTokenClaims));
        const key = JSON.parse(JSON.stringify(idTokenClaims));
        localStorage.setItem('countryCode', key.ctry);
      }
      if (idTokens) {
        localStorage.setItem('TokenKey', JSON.stringify(idTokens));
      }
    }
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

}
